// src/pages/LoginPage.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  ArrowRight
} from 'lucide-react';
import dvsLogo from '../assets/branding/dva_logo.png';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, logout, validateToken } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Step 1: Firebase Authentication
      const user = await login(email, password);
      console.log('✓ Firebase auth successful:', user.uid);

      // Step 2: Get JWT token
      const token = await user.getIdToken();
      console.log('✓ JWT token obtained');

      // Step 3: Validate token with backend (creates session document)
      try {
        await validateToken(token, user.uid);
        console.log('✓ Session validated');

        // Step 4: Navigate to EA personalization on success
        navigate('/ea/personalization');
      } catch (sessionError: any) {
        console.error('Session validation failed:', sessionError);

        // Block access if session validation fails
        setError(sessionError.message || 'Session validation failed. Please try again.');

        // Sign out user since session validation failed
        await logout();
      }
    } catch (err: any) {
      console.error('Login error:', err);

      // User-friendly error messages for Firebase auth errors
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later');
      } else {
        setError('Failed to sign in. Please check your credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-white to-[#18365E]/5 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">

        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-6">
            <img
              src={dvsLogo}
              alt="DVA Logo"
              className="h-24 w-auto"
            />
          </div>
          <h1
            className="text-3xl mb-2"
            style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}
          >
            Welcome Back
          </h1>
          <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
            Sign in to your DVA account
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700" style={{ fontWeight: 500 }}>
                {error}
              </p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleEmailLogin} className="space-y-5">

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm mb-2 text-[#18365E]"
                style={{ fontWeight: 600 }}
              >
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                  style={{ fontWeight: 500 }}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm mb-2 text-[#18365E]"
                style={{ fontWeight: 600 }}
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent text-[#18365E] transition-all"
                  style={{ fontWeight: 500 }}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717182] hover:text-[#18365E] transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-[#FF6E4C] hover:underline"
                style={{ fontWeight: 500 }}
              >
                Forgot password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#18365E] to-[#18365E]/90 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontWeight: 600 }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-[#FF6E4C] hover:underline"
                style={{ fontWeight: 600 }}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
            By signing in, you agree to our{' '}
            <a href="#" className="text-[#18365E] hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-[#18365E] hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
